import React, { useState, useEffect } from 'react';
import LoginModal from './LoginModal';
import NotificationBell from './NotificationBell';
import hygeaLogo from '../../assets/hygea.png';

const Navbar = ({ clientUser, onAuthChange, isSidebarOpen = false, onMenuOpen }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isClientLoggedIn = (clientUser?.role || '').toLowerCase() === 'client';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on scroll
  useEffect(() => {
    if (isMobileMenuOpen) setIsMobileMenuOpen(false);
  }, [isScrolled]);

  const navLinkClass = `text-sm font-semibold transition-colors px-3 py-1 ${
    isScrolled ? 'text-gray-700' : 'text-white'
  } hover:text-[#B89F80]`;

  const navLinks = (
    <>
      <a href="#home" className={navLinkClass} onClick={() => setIsMobileMenuOpen(false)}>Home</a>
      <a href="#services" className={navLinkClass} onClick={() => setIsMobileMenuOpen(false)}>Services</a>
      <a href="#therapists" className={navLinkClass} onClick={() => setIsMobileMenuOpen(false)}>Therapists</a>
      <a href="#contact" className={navLinkClass} onClick={() => setIsMobileMenuOpen(false)}>Contact</a>
    </>
  );

  return (
    <>
      <nav
        className={`fixed left-0 top-0 z-50 w-full transition-all duration-300 ${
          isScrolled || isMobileMenuOpen ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="flex w-full items-center justify-between py-4 px-4 sm:px-6">

          {/* Left: Hamburger (client) + Logo */}
          <div className="flex items-center gap-3">
            {isClientLoggedIn && (
              <button
                type="button"
                aria-label="Open menu"
                onMouseEnter={() => onMenuOpen && onMenuOpen(true)}
                onClick={() => onMenuOpen && onMenuOpen(true)}
                className={`flex h-10 w-10 items-center justify-center rounded-2xl border ${
                  isScrolled || isMobileMenuOpen
                    ? 'border-[#eaded5] bg-white text-[#6f4a42]'
                    : 'border-white/40 bg-white/10 text-white'
                } shadow-[0_10px_25px_rgba(33,24,18,0.12)] transition`}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 7h16" />
                  <path d="M4 12h16" />
                  <path d="M4 17h16" />
                </svg>
              </button>
            )}
            <img
              src={hygeaLogo}
              alt="Hygea"
              className={`h-10 object-contain transition-all ${
                isScrolled || isMobileMenuOpen ? '' : 'brightness-0 invert'
              }`}
            />
          </div>

          {/* Center/Right: Desktop nav links */}
          <div className="hidden md:flex items-center gap-2">
            {navLinks}

            {isClientLoggedIn && <NotificationBell isScrolled={isScrolled} />}

            {!isClientLoggedIn && (
              <button
                onClick={() => setIsLoginModalOpen(true)}
                className="bg-[#4A4A4A] text-white px-6 py-2 rounded-full text-sm font-semibold hover:bg-black transition-all"
              >
                Sign in
              </button>
            )}
          </div>

          {/* Mobile right: notification bell + hamburger toggle — matching style */}
          <div className="flex md:hidden items-center gap-2">
            {isClientLoggedIn && (
              <div className={`flex h-10 w-10 items-center justify-center rounded-2xl border shadow-[0_10px_25px_rgba(33,24,18,0.12)] transition ${
                isScrolled || isMobileMenuOpen
                  ? 'border-[#eaded5] bg-white text-[#6f4a42]'
                  : 'border-white/40 bg-white/10 text-white'
              }`}>
                <NotificationBell isScrolled={isScrolled || isMobileMenuOpen} />
              </div>
            )}

            {!isClientLoggedIn && (
              <button
                onClick={() => setIsLoginModalOpen(true)}
                className="bg-[#4A4A4A] text-white px-4 py-1.5 rounded-full text-xs font-semibold hover:bg-black transition-all"
              >
                Sign in
              </button>
            )}

            {/* Mobile menu toggle — matches sidebar hamburger style */}
            <button
              type="button"
              aria-label="Toggle menu"
              onClick={() => setIsMobileMenuOpen(prev => !prev)}
              className={`flex h-10 w-10 items-center justify-center rounded-2xl border shadow-[0_10px_25px_rgba(33,24,18,0.12)] transition ${
                isScrolled || isMobileMenuOpen
                  ? 'border-[#eaded5] bg-white text-[#6f4a42]'
                  : 'border-white/40 bg-white/10 text-white'
              }`}
            >
              <svg
                className={`h-5 w-5 transition-transform duration-300 ${isMobileMenuOpen ? 'rotate-180' : ''}`}
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              >
                <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 bg-white border-t border-gray-100 ${
            isMobileMenuOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="flex flex-col px-4 py-3 gap-1">
            <a href="#home" className="text-sm font-semibold text-gray-700 hover:text-[#B89F80] py-2.5 border-b border-gray-50 transition-colors" onClick={() => setIsMobileMenuOpen(false)}>Home</a>
            <a href="#services" className="text-sm font-semibold text-gray-700 hover:text-[#B89F80] py-2.5 border-b border-gray-50 transition-colors" onClick={() => setIsMobileMenuOpen(false)}>Services</a>
            <a href="#therapists" className="text-sm font-semibold text-gray-700 hover:text-[#B89F80] py-2.5 border-b border-gray-50 transition-colors" onClick={() => setIsMobileMenuOpen(false)}>Therapists</a>
            <a href="#contact" className="text-sm font-semibold text-gray-700 hover:text-[#B89F80] py-2.5 transition-colors" onClick={() => setIsMobileMenuOpen(false)}>Contact</a>
          </div>
        </div>
      </nav>

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLoginSuccess={(user) => {
          if ((user?.role || '').toLowerCase() === 'client' && onAuthChange) {
            onAuthChange(user);
          }
        }}
      />
    </>
  );
};

export default Navbar;